import React, { useState, useRef, Fragment } from "react"
import { Image } from "react-bootstrap"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { string, object, array } from "yup"
import { useTranslation } from "react-i18next"
import imageCompression from "browser-image-compression"
import { HiLocationMarker, HiPhone, HiTrash, HiOutlinePhotograph, HiX } from "react-icons/hi"
import { Switch } from "@headlessui/react"
import PostcodeSelect from "../../../components/postcodeSelect"
import SearchSelectMultiple from "../../../components/searchSelectMultiple"
import DragZone from "../../../components/dragZone"
import Tooltip from "../../../components/tooltip"
import Emulator from "../../../components/emulator"
import type { FormikProps, FormikHelpers } from "formik"
import { useActivity, useActivityCategories, useUpdateActivity } from "../../../hooks/useActivities"
import { useSelectedProject } from "../../../contexts/SelectedProjectContext"
import { withHttp, capitalize, twClassNames, getLocaleValue } from "../../../helpers"
import { MAX_IMAGE_FILE_SIZE } from "../../../constants/fileHandling"
import type { ActivitySubcategory } from "../../../constants/types"

type ActivityProfileFormValues = {
  host: string | null | undefined
  streetName: string
  city: string
  postalCode: string
  phone: string | null | undefined
  nameFi: string
  nameEn: string
  descriptionFi: string
  descriptionEn: string
  image: string | File | null | undefined
  url: string | null | undefined
  linkTitle: string
  categories: string[]
  visitFinlandId: string | null | undefined
  visitFinlandIntegration: boolean
}

const validationSchema = (t: (key: string) => string) => {
  return object().shape(
    {
      streetName: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required")),
      postalCode: string().required(t("common.validation.required")),
      phone: string()
        .nullable()
        .min(6, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength"))
        .matches(/^\+(?:[0-9]?){6,14}[0-9]$/, t("common.validation.invalidPhone")),
      nameEn: string()
        .when(["nameFi"], {
          is: (nameFi: string) => !nameFi,
          then: () => string().required(t("common.validation.eitherRequired"))
        })
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength")),
      nameFi: string()
        .when(["nameEn"], {
          is: (nameEn: string) => !nameEn,
          then: () => string().required(t("common.validation.eitherRequired"))
        })
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength")),
      descriptionEn: string().when(["descriptionFi"], {
        is: (descriptionFi: string) => !descriptionFi,
        then: () => string().required(t("common.validation.eitherRequired"))
      }),
      descriptionFi: string().when(["descriptionEn"], {
        is: (descriptionEn: string) => !descriptionEn,
        then: () => string().required(t("common.validation.eitherRequired"))
      }),
      categories: array().of(string().required()).min(1, t("common.validation.required"))
    },
    [
      ["nameFi", "nameEn"],
      ["descriptionFi", "descriptionEn"]
    ]
  )
}

const Preview = ({
  data,
  t
}: {
  data: ActivityProfileFormValues
  t: (key: string, opts?: Record<string, unknown>) => string
}) => {
  const [lng, setPreviewLanguage] = useState("fi")

  return (
    <Emulator lng={lng} setPreviewLanguage={setPreviewLanguage}>
      {/* Image */}
      <div className="tw-relative tw-h-[247px] tw-w-full tw-bg-dark-25 tw-rounded-t-[25px]">
        <div
          className="tw-h-[25px] tw-w-full tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-rounded-t-[25px]"
        />
        {data.image && (
          <img
            alt=""
            src={typeof data.image === "string" ? data.image : URL.createObjectURL(data.image)}
            className="tw-h-full tw-w-full tw-object-cover tw-rounded-t-[25px]"
          />
        )}
      </div>
      {/* Text */}
      <div className="tw-h-full tw-w-full tw-flex tw-flex-col tw-px-4">
        <span className="tw-text-xs tw-text-dark-50 tw-mb-2">{data.host}</span>
        <span className="tw-font-semibold tw-font-display tw-text-lg tw-leading-tight tw-mb-4">
          {getLocaleValue(data, "name", lng)}
        </span>
        <div className="tw-mb-2">
          {data.streetName && (
            <p className="tw-space-x-2 tw-mb-0">
              <HiLocationMarker className="tw-text-primary" />
              <span className="tw-text-sm">
                {data.streetName}, {data?.postalCode} {capitalize(data?.city)}
              </span>
            </p>
          )}
          {data.phone && (
            <p className="tw-space-x-2 tw-mb-0">
              <HiPhone className="tw-text-primary" />
              <span className="tw-text-sm">{data.phone}</span>
            </p>
          )}
        </div>
        {getLocaleValue(data, "description", lng) && (
          <div className="tw-mb-2">
            <p className="tw-font-semibold tw-font-display tw-text-sm tw-mb-2">
              {t("activities.profile.emulator.description", { lng })}
            </p>
            <p className="tw-text-sm tw-text-dark-75 tw-line-clamp-6">
              {getLocaleValue(data, "description", lng) as string}
            </p>
          </div>
        )}
        {data.url && (
          <button className="tw-w-full tw-btn tw-btn-primary" type="button">
            {t("activities.profile.form.linkTitleOptions." + data.linkTitle.toLowerCase(), { lng })}
          </button>
        )}
      </div>
    </Emulator>
  )
}

const Form = ({
  formik,
  categories,
  isLoading,
  t
}: {
  formik: FormikProps<ActivityProfileFormValues>
  categories: ActivitySubcategory[]
  isLoading: boolean
  t: (key: string) => string
}) => {
  const { i18n } = useTranslation()
  const imageRef = useRef<HTMLInputElement>(null)

  const onReset = () => {
    formik.handleReset()
    if (imageRef.current) imageRef.current.value = ""
  }

  const isValidCLass = (field: keyof ActivityProfileFormValues) =>
    formik.touched[field] && formik.errors[field] && "tw-is-invalid"

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <div className="tw-w-full md:tw-pr-6 max-md:tw-pb-6">
        {!!formik.values.visitFinlandId && (
          <div className="tw-bg-light tw-p-4 tw-rounded-2xl tw-mb-8">
            {/* Business Finland Datahub informations */}
            <div className="tw-relative tw-col-span-6">
              <h5>{t("activities.profile.form.datahubSubtitle.name")}</h5>
              <p className="tw-text-sm tw-text-dark-75">{t("activities.profile.form.datahubSubtitle.info")}</p>
            </div>
            {/* Integration */}
            <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
              <label htmlFor="visitFinlandIntegration" className="tw-form-label">
                {t("activities.profile.form.integration")}
              </label>
              <div className="tw-flex tw-space-x-2">
                <Switch
                  checked={formik.values.visitFinlandIntegration}
                  onChange={(value: boolean) => formik.setFieldValue("visitFinlandIntegration", value)}
                  as={Fragment}>
                  {({ checked }: { checked: boolean }) => (
                    <button
                      className={twClassNames(
                        checked ? "tw-bg-primary" : "tw-bg-dark-25",
                        "tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-items-center tw-rounded-full",
                        "focus-within:tw-outline-none focus-within:tw-ring-2 focus-within:tw-ring-primary-50"
                      )}>
                      <span
                        className={twClassNames(
                          checked ? "tw-translate-x-6" : "tw-translate-x-1",
                          "tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition"
                        )}
                      />
                    </button>
                  )}
                </Switch>
              </div>
            </div>
          </div>
        )}
        <fieldset
          disabled={formik.values.visitFinlandIntegration}
          className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
          {/* Host informations */}
          <div className="tw-relative tw-col-span-6 tw-border-b">
            <h5>{t("activities.profile.form.hostSubtitle.name")}</h5>
            <p className="tw-text-sm tw-text-dark-75">{t("activities.profile.form.hostSubtitle.info")}</p>
          </div>
          {/* Host name */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="host" className="tw-form-label">
              {t("activities.profile.form.host")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="activities.profile.tooltips.host" displayLabel />
            </label>
            <input
              type="text"
              id="host"
              name="host"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.host ?? ""}
              className={twClassNames(isValidCLass("host"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.host}</div>
          </div>
          {/* Street Name */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="streetName" className="tw-form-label">
              {t("activities.profile.form.streetName")}
            </label>
            <input
              type="text"
              id="streetName"
              name="streetName"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.streetName}
              className={twClassNames(isValidCLass("streetName"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.streetName}</div>
          </div>
          {/* Postal Code */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="postalCode" className="tw-form-label">
              {t("activities.profile.form.postalCode")}
            </label>
            <PostcodeSelect
              id="postalCode"
              value={formik.values.postalCode}
              onBlur={formik.handleBlur}
              onChange={(value) => {
                formik.setFieldValue("city", value.city)
                formik.setFieldValue("postalCode", value.postcode)
              }}
              errors={formik.touched.postalCode ? formik.errors.postalCode : undefined}
            />
          </div>
          {/* City */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="city" className="tw-form-label">
              {t("activities.profile.form.city")}
              <Tooltip message="activities.profile.tooltips.city" />
            </label>
            <input value={formik.values.city} className="tw-hidden tw-peer" readOnly />
            <p
              className="tw-block tw-w-full tw-pt-2 tw-px-3 tw-capitalize tw-text-base sm:tw-text-sm
                peer-disabled:tw-text-dark-50 tw-value-field">
              {formik.values.city}
            </p>
          </div>
          {/* Phone */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="phone" className="tw-form-label">
              {t("activities.profile.form.phone")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
            </label>
            <input
              type="phone"
              id="phone"
              name="phone"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone ?? ""}
              className={twClassNames(isValidCLass("phone"), "tw-form-control")}
            />
            <div className="tw-invalid-tooltip">{formik.errors.phone}</div>
          </div>
          {/* Activities informations */}
          <div className="tw-relative tw-col-span-6 tw-border-b">
            <h5 className="tw-mt-4">{t("activities.profile.form.activitySubtitle.name")}</h5>
            <p className="tw-text-sm tw-text-dark-75">{t("activities.profile.form.activitySubtitle.info")}</p>
          </div>
          {/* Name FI */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="nameFi" className="tw-form-label">
              {t("activities.profile.form.nameFi")}
              <Tooltip message="activities.profile.tooltips.nameFi" />
            </label>
            <input
              type="text"
              id="nameFi"
              name="nameFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameFi}
              className={twClassNames(isValidCLass("nameFi"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameFi}</div>
          </div>
          {/* Name EN */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="nameEn" className="tw-form-label tw-flex tw-justify-betweesn">
              {t("activities.profile.form.nameEn")}
            </label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameEn}
              className={twClassNames(isValidCLass("nameEn"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameEn}</div>
          </div>
          {/* Description FI */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionFi" className="tw-form-label">
              {t("activities.profile.form.descriptionFi")}
              <Tooltip message="activities.profile.tooltips.descriptionFi" />
            </label>
            <textarea
              id="descriptionFi"
              name="descriptionFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionFi}
              className={twClassNames(isValidCLass("descriptionFi"), "tw-form-control tw-min-h-[150px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionFi}</div>
          </div>
          {/* Description EN */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionEn" className="tw-form-label">
              {t("activities.profile.form.descriptionEn")}
            </label>
            <textarea
              id="descriptionEn"
              name="descriptionEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionEn}
              className={twClassNames(isValidCLass("descriptionEn"), "tw-form-control tw-min-h-[150px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionEn}</div>
          </div>
          {/* Categories */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="categories" className="tw-form-label">
              {t("activities.profile.form.categories")}
              <Tooltip message="activities.profile.tooltips.categories" />
            </label>
            <SearchSelectMultiple
              id="categories"
              data={categories}
              nameKey={"name" + capitalize(i18n.resolvedLanguage ?? "en")}
              valueKey="id"
              indexKey="id"
              placeholder={t("activities.profile.placeholders.categories")}
              onChange={(value: string[]) => formik.setFieldValue("categories", value)}
              onBlur={formik.handleBlur}
              value={formik.values.categories}
              errors={formik.touched.categories ? (formik.errors.categories as string) : undefined}
            />
          </div>
          <div className="tw-relative tw-col-span-6 tw-flex tw-flex-wrap">
            {formik.values.categories &&
              categories
                .filter((x: { id: string }) => formik.values.categories.indexOf(x.id) !== -1)
                .map((tag: { id: string; name: string }, index: number) => (
                  <div
                    key={index}
                    className="tw-bg-dark-25 tw-text-dark tw-px-3 tw-py-1 tw-mr-1 tw-mb-2 tw-rounded tw-text-sm
                      tw-cursor-default tw-min-w-fit">
                    {tag.name}
                    <HiX
                      className="tw-ml-1 tw-mb-0.5 tw-cursor-pointer tw-align-middle"
                      onClick={() =>
                        formik.setFieldValue(
                          "categories",
                          formik.values.categories.filter((x: string) => x !== tag.id)
                        )
                      }
                    />
                  </div>
                ))}
          </div>
          {/* Url */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="url" className="tw-form-label">
              {t("activities.profile.form.url")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
            </label>
            <input
              type="text"
              id="url"
              name="url"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.url ?? ""}
              className={twClassNames(isValidCLass("url"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.url}</div>
          </div>
          {/* link title */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="linkTitle" className="tw-form-label">
              {t("activities.profile.form.linkTitle")}
            </label>
            <select
              id="linkTitle"
              name="linkTitle"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.linkTitle}
              className={twClassNames(isValidCLass("linkTitle"), "tw-form-control")}>
              <option value="readmore">{t("activities.profile.form.linkTitleOptions.readmore")}</option>
              <option value="book">{t("activities.profile.form.linkTitleOptions.book")}</option>
              <option value="contact">{t("activities.profile.form.linkTitleOptions.contact")}</option>
            </select>
            <div className="tw-invalid-tooltip">{formik.errors.linkTitle}</div>
          </div>
          {/* Images */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="image" className="tw-form-label">
              {t("activities.profile.form.image")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
            </label>
            {formik.values.image ? (
              <div className="tw-relative tw-w-fit">
                <Image
                  thumbnail
                  onClick={() => imageRef.current?.click()}
                  className="background-thumbnail"
                  src={
                    typeof formik.values.image === "string"
                      ? formik.values.image
                      : URL.createObjectURL(formik.values.image)
                  }
                />
                <button
                  type="button"
                  onClick={() => formik.setFieldValue("image", null)}
                  className="tw-absolute tw-bottom-0 tw-right-4 tw-bg-red-400 tw-rounded-full disabled:tw-opacity-50">
                  <HiTrash className="tw-text-white tw-h-8 tw-w-8 tw-p-2" />
                </button>
              </div>
            ) : (
              <DragZone
                onDrop={(data: File) => formik.setFieldValue("image", data)}
                onClick={() => imageRef.current?.click()}
                className="tw-flex tw-justify-center tw-items-center tw-rounded-md tw-px-6 tw-py-4 tw-border-2
                  tw-border-dashed tw-cursor-pointer">
                <div className="tw-space-y-1 tw-text-center">
                  <HiOutlinePhotograph className="tw-mx-auto tw-h-12 tw-w-12 tw-text-dark-25 [&>*]:tw-stroke-1" />
                  <div className="tw-flex tw-text-sm tw-text-dark-75">
                    <label
                      htmlFor="image"
                      className="tw-relative tw-rounded-md tw-bg-white tw-font-medium tw-text-primary">
                      <span>{t("common.components.dragZone.upload")}</span>
                    </label>
                    <p className="tw-pl-1">{t("common.components.dragZone.dragAndDrop")}</p>
                  </div>
                  <p className="tw-text-xs tw-text-dark-50">{t("common.components.dragZone.type")}</p>
                </div>
              </DragZone>
            )}
            <input
              type="file"
              name="image"
              accept="image/*"
              className="tw-hidden tw-sr-only"
              ref={imageRef}
              onChange={event => formik.setFieldValue("image", event.currentTarget.files?.[0])}
            />
            <div className="tw-invalid-tooltip">{formik.errors.image}</div>
          </div>
        </fieldset>
      </div>
      {formik.dirty && (
        <div className="tw-fixed-footer tw-has-sidebar tw-bg-white tw-shadow-2xl tw-border-t tw-z-10 tw-py-4">
          <div className="tw-px-6 tw-flex tw-w-full tw-items-center tw-justify-between">
            <span>{t("common.unsavedChanges.content")}</span>
            <div className="tw-flex tw-flex-row tw-space-x-4">
              <button
                type="reset"
                disabled={isLoading}
                onClick={onReset}
                className="tw-btn tw-btn-outline tw-min-w-[180px]">
                {t("common.unsavedChanges.button.cancel")}
              </button>
              <button type="submit" disabled={isLoading} className="tw-btn tw-btn-primary tw-min-w-[180px]">
                {t("common.unsavedChanges.button.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}

const ActivitiesProfileContainer: React.FC = () => {
  const { t } = useTranslation()
  const { selectedActivityId } = useSelectedProject()
  const { data: activity } = useActivity(selectedActivityId)
  const { data: activityCategories = [] } = useActivityCategories()
  const updateActivity = useUpdateActivity()

  const onSave = async (
    { image, ...values }: ActivityProfileFormValues,
    actions: FormikHelpers<ActivityProfileFormValues>
  ) => {
    const data: Record<string, unknown> = {
      ...values,
      url: withHttp(values.url),
      name: values.nameEn || values.nameFi,
      description: values.descriptionEn || values.descriptionFi
    }

    if (image instanceof File) {
      const filesize = image.size / 1024 / 1024
      const options = {
        maxSizeMB: MAX_IMAGE_FILE_SIZE,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.6
      }
      data.image = filesize > MAX_IMAGE_FILE_SIZE ? await imageCompression(image, options) : image
    } else if (image === null) {
      data.deleteImage = true
    }

    try {
      await updateActivity.mutateAsync({ id: activity?.id as string, data })
      actions.resetForm()
      toast.success(t("settings.notifications.save.success"))
    } catch {
      toast.error(t("settings.notifications.save.failed"))
    }
  }

  const formik = useFormik<ActivityProfileFormValues>({
    initialValues: {
      host: activity?.host,
      streetName: activity?.streetName || "",
      city: activity?.city || "",
      postalCode: activity?.postalCode || "",
      phone: activity?.phone,
      nameFi: activity?.nameFi || "",
      nameEn: activity?.nameEn || "",
      descriptionFi: activity?.descriptionFi || "",
      descriptionEn: activity?.descriptionEn || "",
      image: activity?.image,
      url: activity?.url,
      linkTitle: activity?.linkTitle || "readmore",
      categories: ((activity as Record<string, unknown>)?.categories as { id: string }[])?.map(c => c.id) || [],
      visitFinlandId: activity?.visitFinlandId,
      visitFinlandIntegration: activity?.visitFinlandIntegration || false
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema: validationSchema(t),
    onSubmit: (values, actions) => onSave(values, actions)
  })

  return (
    <>
      <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("activities.profile.title")}</h1>
      <div className={twClassNames(formik.dirty ? "tw-mb-20" : "tw-mb-12", "tw-w-full")}>
        <div className="tw-mb-6 tw-shadow tw-bg-white tw-rounded">
          <div className="tw-flex tw-p-6 max-md:tw-flex-col">
            {/* Left side */}
            <div className="tw-w-full md:tw-w-1/2">
              <Form formik={formik} categories={activityCategories} isLoading={updateActivity.isPending} t={t} />
            </div>
            {/* Right side */}
            <div className="tw-w-full md:tw-w-1/2 tw-min-w-[360px] max-xs:tw-hidden">
              <Preview data={formik.values} t={t} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ActivitiesProfileContainer
